import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { WizardProps } from '@amzn/awsui-components-core';

interface UseBaseballCardWizardArgs {
  /**
   * Per-step forward gate: `stepGates[i]` is whether the user may advance PAST step `i`.
   * A `true` (or missing) entry lets the user leave that step going forward; `false` blocks
   * with `blockedMessages[i]`. Backward navigation (previous / earlier step link) is always
   * allowed regardless of gates.
   */
  stepGates: boolean[];
  /** Message shown as the step's error alert when its forward gate blocks. */
  blockedMessages: string[];
  /**
   * Total number of wizard steps. Used to seed Cloudscape's internal "furthest step reached"
   * high-water mark on mount so EVERY step renders as a clickable nav link from the start (see
   * the mount effect below). Passed explicitly rather than inferred from `stepGates.length`,
   * which counts the gaps BETWEEN steps (stepCount - 1), not the steps themselves.
   */
  stepCount: number;
  /** Called when the user submits on the final step (Generate PDFs). */
  onSubmit: () => void;
}

interface UseBaseballCardWizardReturn {
  activeStepIndex: number;
  setActiveStepIndex: (index: number) => void;
  stepError: string;
  onNavigate: (detail: WizardProps.NavigateDetail) => void;
  /**
   * Programmatically jump to a step, bypassing the forward gates. Use only when the target step's
   * prerequisite has just been satisfied by a side-effect (e.g. WorkDocs provisioning succeeded),
   * not for user-driven navigation — that still flows through `onNavigate` and its gates. Clears
   * any stale `stepError` left by an earlier blocked Next.
   */
  goToStep: (index: number) => void;
  onSubmit: () => void;
}

/**
 * Drives the Baseball Cards wizard: owns `activeStepIndex` and gates forward navigation.
 * Mirrors the admin homepage wizard's controlled pattern, but validates with plain boolean
 * predicates (token present, folders provisioned) rather than a Formik/Yup schema — this
 * flow is a stateful pipeline, not a form.
 *
 * Only the Next BUTTON (`reason: 'next'`) is gated: advancing past a step requires every step
 * being passed to have a satisfied gate, and the first unmet gate sets `stepError` and blocks.
 * Left-nav step-link clicks (`reason: 'step'`), the skip-to action (`reason: 'skip'`), and
 * backward moves (`reason: 'previous'`) are ALWAYS allowed and jump freely — the user can click
 * any step in the navigation pane at will, even a not-yet-reached one (those steps render their
 * own "not ready" empty states). The Next button's grey-out (driven separately by `stepGates` in
 * the page) still signals per-step readiness.
 */
export const useBaseballCardWizard = ({
  stepGates,
  blockedMessages,
  stepCount,
  onSubmit
}: UseBaseballCardWizardArgs): UseBaseballCardWizardReturn => {
  // Seed Cloudscape's grow-only nav high-water mark so EVERY step is a clickable left-nav link from
  // the start. The Wizard renders a step as a clickable link only once its internal
  // `farthestStepIndex` (a grow-only ref = Math.max over every COMMITTED activeStepIndex) reaches
  // that step; an unreached step (e.g. Step 4 while on Step 3) renders as a disabled span with no
  // onClick, so the user can't jump ahead. There is no prop to seed that ref, so we make the FIRST
  // committed render sit on the LAST step — Cloudscape initializes the ref to that index — then the
  // layout effect below snaps back to step 0 BEFORE the browser paints. The last step is committed
  // (growing the ref) but never painted (useLayoutEffect runs pre-paint), so there's no visible
  // flash; and because the ref only grows, every step stays clickable for the rest of the session.
  //
  // The seed lives in the INITIAL useState value, not a state update inside the effect. A state
  // update can't do it: inside any effect React is mid-commit (executionContext has CommitContext),
  // and even flushSync only performs its synchronous flush when executionContext === NoContext — so
  // flushSync(() => setActiveStepIndex(stepCount - 1)) leaves the update pending, it batches with
  // the reset, and both collapse into a single committed render at 0 (the original bug). Seeding via
  // the initial render value sidesteps batching entirely.
  const [activeStepIndex, setActiveStepIndex] = useState(stepCount > 1 ? stepCount - 1 : 0);
  const [stepError, setStepError] = useState('');

  const hasSeededNav = useRef(false);
  useLayoutEffect(() => {
    // Snap back to step 0 after the seeded last-step render commits but before paint. Ref-guarded so
    // it runs once even under StrictMode's dev double-invoke of effects (the ref persists across the
    // simulated remount); a repeat reset to 0 would be a harmless no-op regardless.
    if (hasSeededNav.current) return;
    hasSeededNav.current = true;
    setActiveStepIndex(0);
  }, []);

  const onNavigate = useCallback(
    (detail: WizardProps.NavigateDetail) => {
      const { requestedStepIndex, reason } = detail;

      // Only the Next button is gated. Navigation-pane clicks ('step'), the skip-to button
      // ('skip'), backward moves ('previous'), and any same/earlier target jump freely.
      if (reason !== 'next' || requestedStepIndex <= activeStepIndex) {
        setStepError('');
        setActiveStepIndex(requestedStepIndex);
        return;
      }

      // Next button moving forward: every step from the current index up to (but not including)
      // the target must have a satisfied gate. Report the first unmet gate's message.
      for (let stepIndex = activeStepIndex; stepIndex < requestedStepIndex; stepIndex++) {
        if (stepGates[stepIndex] === false) {
          setStepError(blockedMessages[stepIndex] ?? 'Complete this step before continuing.');
          return;
        }
      }

      setStepError('');
      setActiveStepIndex(requestedStepIndex);
    },
    [activeStepIndex, stepGates, blockedMessages]
  );

  const goToStep = useCallback((index: number) => {
    setStepError('');
    setActiveStepIndex(index);
  }, []);

  return { activeStepIndex, setActiveStepIndex, stepError, onNavigate, goToStep, onSubmit };
};
